import { createTheme, Theme } from '@mui/material/styles';
import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  generateColorPalette,
  getContrastTextColor,
  hexToRgba,
} from './brandTheme';

const QUICKSAND = '"Quicksand", Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const buildShades = (hex: string) => generateColorPalette(hex);

export interface MuiBrandColors {
  primaryColor?: string;
  secondaryColor?: string;
}

/**
 * MUI theme that LOOKS LIKE HeroUI — same MUI components, HeroUI visual language.
 * Reference screenshots: pill tabs, soft 16-20px cards, #f4f4f5 sub-bg, full-pill buttons, 200ms motions.
 * Keeps API-driven primary (CTA) + secondary (header AppBar) reactivity.
 */
export const createMuiBrandTheme = ({
  primaryColor = DEFAULT_PRIMARY_COLOR,
  secondaryColor = DEFAULT_SECONDARY_COLOR,
}: MuiBrandColors): Theme => {
  const primaryPalette = buildShades(primaryColor);
  const secondaryPalette = buildShades(secondaryColor);
  const primaryContrast = getContrastTextColor(primaryColor);
  const secondaryContrast = getContrastTextColor(secondaryColor);

  return createTheme({
    typography: {
      fontFamily: QUICKSAND,
      h1: { fontFamily: QUICKSAND, fontWeight: 700, fontSize: '1.7rem', letterSpacing: '-0.02em', lineHeight: 1.2 },
      h2: { fontFamily: QUICKSAND, fontWeight: 700, fontSize: '1.3rem', letterSpacing: '-0.015em' },
      h3: { fontFamily: QUICKSAND, fontWeight: 700, fontSize: '1.0rem' },
      h4: { fontFamily: QUICKSAND, fontWeight: 600, fontSize: '0.92rem' },
      button: { fontFamily: QUICKSAND, fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
      body1: { fontFamily: QUICKSAND, fontSize: '0.875rem', lineHeight: 1.6, color: '#18181b' },
      body2: { fontFamily: QUICKSAND, fontSize: '0.82rem', color: '#71717a' },
      caption: { fontFamily: QUICKSAND, fontSize: '0.72rem', letterSpacing: '0.02em' },
    },
    spacing: 8,
    shape: { borderRadius: 6 },
    palette: {
      mode: 'light',
      primary: {
        main: primaryColor,
        light: primaryPalette[200].hex,
        dark: primaryPalette[700].hex,
        contrastText: primaryContrast,
        '50': primaryPalette[50].hex,
        '100': primaryPalette[100].hex,
        '200': primaryPalette[200].hex,
        '300': primaryPalette[300].hex,
        '400': primaryPalette[400].hex,
        '500': primaryPalette[500].hex,
        '600': primaryPalette[600].hex,
        '700': primaryPalette[700].hex,
        '800': primaryPalette[800].hex,
        '900': primaryPalette[900].hex,
      } as any,
      secondary: {
        main: secondaryColor,
        light: secondaryPalette[200].hex,
        dark: secondaryPalette[700].hex,
        contrastText: secondaryContrast,
      },
      background: { default: '#f8f8f9', paper: '#ffffff' },
      text: { primary: '#18181b', secondary: '#71717a' },
      divider: '#e4e4e7',
      grey: { 100: '#f4f4f5', 200: '#e4e4e7', 300: '#d4d4d8', 500: '#71717a' } as any,
      success: { main: '#17c964', light: '#e8f5e9', contrastText: '#0a4a1e' },
      error: { main: '#f31260', light: '#fee7ef', contrastText: '#fff' },
      warning: { main: '#f5a524', light: '#fef3c7', contrastText: '#78350f' },
      info: { main: '#006FEE', light: '#e6f1fe', contrastText: '#fff' },
    },
    shadows: [
      'none',
      '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      '0 1px 3px 0 rgb(0 0 0 / 0.05)',
      '0 4px 6px -1px rgb(0 0 0 / 0.04)',
      '0 8px 10px -1px rgb(0 0 0 / 0.04)',
      '0 10px 15px -2px rgb(0 0 0 / 0.06)',
      '0 12px 20px -4px rgb(0 0 0 / 0.07)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
      '0 16px 24px -6px rgb(0 0 0 / 0.08)',
    ] as any,
    transitions: {
      duration: { shortest: 150, shorter: 180, short: 200, standard: 250, complex: 300, enteringScreen: 220, leavingScreen: 180 },
      easing: { easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)', easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)', easeIn: 'cubic-bezier(0.4, 0, 1, 1)', sharp: 'cubic-bezier(0.4, 0, 0.6, 1)' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { scrollbarGutter: 'stable' as any },
          body: { fontFamily: QUICKSAND, backgroundColor: '#f8f8f9', color: '#18181b' },
          '*': { scrollbarWidth: 'thin' as any, scrollbarColor: '#d4d4d8 transparent' },
          '::selection': { backgroundColor: hexToRgba(primaryColor, 0.18) },
          '::-webkit-scrollbar': { width: 6, height: 6 },
          '::-webkit-scrollbar-thumb': { background: '#d4d4d8', borderRadius: 9999 },
        },
      },
      // Header = secondaryColor (API navbar color), heroUI-style 48-56px, no shadow, subtle blur
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            backgroundColor: secondaryColor,
            color: secondaryContrast,
            boxShadow: 'none',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            backdropFilter: 'saturate(180%) blur(8px)',
            transition: 'all 200ms cubic-bezier(0,0,0.2,1)',
          },
        },
      },
      // HeroUI pill buttons: 32-36px, 9999 radius, 13px/14px, motion 200ms, scale on press
      MuiButton: {
        defaultProps: { disableElevation: true, disableRipple: true },
        styleOverrides: {
          root: {
            borderRadius: '6px',
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: QUICKSAND,
            fontSize: '0.8125rem',
            lineHeight: 1.2,
            minHeight: 32,
            padding: '8px 16px',
            transition: 'all 200ms cubic-bezier(0,0,0.2,1)',
            '&:active': { transform: 'scale(0.98)' },
          },
          sizeSmall: { minHeight: 28, padding: '4px 12px', fontSize: '0.76rem' },
          sizeLarge: { minHeight: 40, padding: '10px 22px', fontSize: '0.875rem' },
          containedPrimary: {
            backgroundColor: primaryColor,
            backgroundImage: 'none',
            color: primaryContrast,
            boxShadow: `0 2px 8px ${hexToRgba(primaryColor, 0.25)}`,
            '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
              color: primaryContrast,
            },
            '&:hover': {
              backgroundColor: primaryPalette[600].hex,
              backgroundImage: 'none',
              color: getContrastTextColor(primaryPalette[600].hex),
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${hexToRgba(primaryColor, 0.35)}`,
              '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
                color: getContrastTextColor(primaryPalette[600].hex),
              },
            },
            '&.Mui-disabled': {
              backgroundColor: '#e4e4e7',
              color: '#a1a1aa',
              backgroundImage: 'none',
            },
          },
          containedSecondary: {
            backgroundColor: secondaryColor,
            backgroundImage: 'none',
            color: secondaryContrast,
            '&:hover': {
              backgroundColor: secondaryPalette[600].hex,
              backgroundImage: 'none',
            },
          },
          containedError: {
            backgroundColor: '#f31260',
            backgroundImage: 'none',
            color: '#ffffff',
            boxShadow: '0 2px 8px rgba(243, 18, 96, 0.25)',
            '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
              color: '#ffffff',
            },
            '&:hover': {
              backgroundColor: '#e10e55',
              backgroundImage: 'none',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(243, 18, 96, 0.35)',
            },
          },
          outlined: {
            borderColor: '#e4e4e7',
            backgroundColor: '#fff',
            color: '#18181b',
            '&:hover': { backgroundColor: '#f4f4f5', borderColor: '#d4d4d8' },
          },
          outlinedPrimary: {
            borderColor: hexToRgba(primaryColor, 0.5),
            color: primaryColor,
            backgroundColor: 'transparent',
            '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
              color: primaryColor,
            },
            '&:hover': {
              backgroundColor: hexToRgba(primaryColor, 0.08),
              borderColor: primaryColor,
              color: primaryPalette[600].hex,
              '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
                color: primaryPalette[600].hex,
              },
            },
          },
          outlinedError: {
            borderColor: 'rgba(243, 18, 96, 0.4)',
            backgroundColor: 'transparent',
            color: '#f31260',
            '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
              color: '#f31260',
            },
            '&:hover': {
              backgroundColor: 'rgba(243, 18, 96, 0.08)',
              borderColor: '#f31260',
              color: '#e10e55',
              '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
                color: '#e10e55',
              },
            },
          },
          text: {
            color: '#18181b',
            '&:hover': { backgroundColor: '#f4f4f5' },
          },
          textPrimary: {
            color: primaryColor,
            '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
              color: primaryColor,
            },
            '&:hover': {
              backgroundColor: hexToRgba(primaryColor, 0.08),
              color: primaryPalette[600].hex,
              '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
                color: primaryPalette[600].hex,
              },
            },
          },
          textError: {
            color: '#f31260',
            backgroundColor: 'transparent',
            '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
              color: '#f31260',
            },
            '&:hover': {
              backgroundColor: 'rgba(243, 18, 96, 0.08)',
              color: '#e10e55',
              '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
                color: '#e10e55',
              },
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: primaryColor,
            textDecoration: 'none',
            transition: 'color 150ms ease',
            '&:hover': {
              color: primaryPalette[600].hex,
              textDecoration: 'underline',
            },
          },
        },
      },
      // HeroUI flat gray buttons / IconButtons
      MuiIconButton: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: {
            borderRadius: '6px',
            transition: 'all 200ms ease',
            '&:hover': { backgroundColor: '#f4f4f5' },
            '&:active': { transform: 'scale(0.96)' },
          },
          colorError: {
            color: '#f31260',
            '& svg': {
              color: '#f31260',
            },
            '&:hover': {
              backgroundColor: 'rgba(243, 18, 96, 0.08)',
              color: '#e10e55',
              '& svg': {
                color: '#e10e55',
              },
            },
          },
          colorPrimary: {
            color: primaryColor,
            '& svg': {
              color: primaryColor,
            },
            '&:hover': {
              backgroundColor: hexToRgba(primaryColor, 0.08),
              color: primaryPalette[600].hex,
              '& svg': {
                color: primaryPalette[600].hex,
              },
            },
          },
        },
      },
      // Paper & Cards: 6px radius, subtle border, flat clean appearance
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
            border: '1px solid #e4e4e7',
            backgroundColor: '#fff',
            backgroundImage: 'none',
            boxShadow: 'none',
            transition: 'all 200ms cubic-bezier(0,0,0.2,1)',
          },
          elevation1: { boxShadow: 'none' },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: '6px',
            border: '1px solid #e4e4e7',
            backgroundColor: '#fff',
            boxShadow: 'none',
            overflow: 'hidden',
            transition: 'all 250ms cubic-bezier(0,0,0.2,1)',
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { padding: '14px 16px 8px' },
          title: { fontFamily: QUICKSAND, fontWeight: 600, fontSize: '0.88rem', color: '#18181b' },
          subheader: { fontFamily: QUICKSAND, fontSize: '0.76rem', color: '#71717a' },
        },
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: '12px 16px 16px', '&:last-child': { paddingBottom: 16 } } },
      },
      // Inputs: HeroUI 40px, 12px radius, #e4e4e7 border, white bg, focus primary
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontFamily: QUICKSAND, fontSize: '0.82rem', color: '#71717a', '&.Mui-focused': { color: primaryColor } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
            fontFamily: QUICKSAND,
            backgroundColor: '#fff',
            fontSize: '0.875rem',
            transition: 'all 150ms ease',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e4e7', borderWidth: 1, transition: 'all 150ms ease' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d8', backgroundColor: '#fcfcfc' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor, borderWidth: 1.5, boxShadow: `0 0 0 3px ${hexToRgba(primaryColor, 0.12)}` },
          },
          input: { padding: '8px 12px', '&::placeholder': { color: '#a1a1aa', opacity: 1 } },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: { fontFamily: QUICKSAND },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 38 },
          indicator: { height: 2, backgroundColor: primaryColor },
          flexContainer: { gap: 4 },
        },
      },
      MuiTab: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: {
            fontFamily: QUICKSAND,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.84rem',
            minHeight: 36,
            padding: '6px 14px',
            borderRadius: '4px',
            color: '#71717a',
            transition: 'all 150ms ease',
            '&:hover': { color: '#18181b' },
            '&.Mui-selected': { color: primaryColor, fontWeight: 600 },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontFamily: QUICKSAND, fontWeight: 500, borderRadius: '4px', fontSize: '0.74rem', height: 22, transition: 'all 150ms ease' },
          filled: { backgroundColor: '#f4f4f5', color: '#18181b', border: '1px solid #e4e4e7' },
          colorSuccess: { backgroundColor: '#e7f8e9', color: '#0a4a1e', borderColor: 'transparent' },
          colorWarning: { backgroundColor: '#fef3c7', color: '#78350f', borderColor: 'transparent' },
          colorError: { backgroundColor: '#fee7ef', color: '#8a0a2e', borderColor: 'transparent' },
          colorPrimary: { backgroundColor: hexToRgba(primaryColor, 0.12), color: primaryColor, borderColor: 'transparent', fontWeight: 600 },
        },
      },
      // Table: HeroUI header tiny uppercase 11px gray, rows rounded, hover #f9f9f9
      MuiTableHead: {
        styleOverrides: {
          root: { '& .MuiTableCell-head': { backgroundColor: '#f9f9f9', fontSize: '0.70rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#71717a', borderBottom: '1px solid #e4e4e7', padding: '10px 12px' } },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { transition: 'background 150ms ease', '&:hover': { backgroundColor: '#fcfcfc' }, '&:last-child td': { borderBottom: 'none' } },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { fontFamily: QUICKSAND, fontSize: '0.8125rem', borderBottom: '1px solid #f0f0f2', padding: '12px', color: '#18181b', textAlign: 'left' },
          head: { textAlign: 'left' },
          body: { color: '#27272a', textAlign: 'left' },
        },
      },
      MuiTableContainer: {
        styleOverrides: { root: { borderRadius: 14, border: '1px solid #e4e4e7', backgroundColor: '#fff' } },
      },
      // Dialog/Drawer = HeroUI 20px radius, motion, always viewport-wide and centered
      MuiDialog: {
        styleOverrides: {
          root: {
            width: '100vw !important',
            maxWidth: 'none !important',
            left: '0 !important',
            right: '0 !important',
            top: '0 !important',
            bottom: '0 !important',
          },
          container: {
            width: '100% !important',
            height: '100% !important',
            display: 'flex !important',
            alignItems: 'center !important',
            justifyContent: 'center !important',
          },
          paper: {
            borderRadius: 20,
            border: '1px solid #e4e4e7',
            boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
            backgroundColor: '#fff',
            marginLeft: 'auto !important',
            marginRight: 'auto !important',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { borderRadius: '16px 0 0 16px', border: 'none', borderLeft: '1px solid #e4e4e7', boxShadow: '-8px 0 24px rgba(0,0,0,0.06)' },
        },
      },
      MuiBackdrop: {
        styleOverrides: { root: { backgroundColor: 'rgba(0,0,0,0.32)', backdropFilter: 'blur(2px)' } },
      },
      // Switch / Slider / Checkbox HeroUI pastel
      MuiSwitch: {
        styleOverrides: {
          root: { padding: 8, '& .MuiSwitch-track': { borderRadius: 9999, backgroundColor: '#e4e4e7', transition: 'all 200ms ease' }, '& .MuiSwitch-thumb': { boxShadow: '0 1px 2px rgba(0,0,0,0.12)', transition: 'all 200ms cubic-bezier(0,0,0.2,1)' } },
          switchBase: { '&.Mui-checked': { color: '#fff', '& + .MuiSwitch-track': { backgroundColor: primaryColor, opacity: 1 } } },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: { height: 6, '& .MuiSlider-rail': { backgroundColor: '#e4e4e7', opacity: 1 }, '& .MuiSlider-track': { backgroundColor: primaryColor, border: 'none' }, '& .MuiSlider-thumb': { width: 18, height: 18, backgroundColor: '#fff', border: `2px solid ${primaryColor}`, boxShadow: '0 1px 3px rgba(0,0,0,0.12)', transition: 'all 200ms ease', '&:hover': { boxShadow: `0 0 0 6px ${hexToRgba(primaryColor, 0.15)}` } } },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 12, border: '1px solid #e4e4e7', fontFamily: QUICKSAND, fontSize: '0.8125rem', alignItems: 'center' },
          standardSuccess: { backgroundColor: '#f0fdf4', borderColor: '#bbf7d0', color: '#0a4a1e' },
          standardError: { backgroundColor: '#fff1f2', borderColor: '#fecdd3', color: '#8a0a2e' },
          standardWarning: { backgroundColor: '#fffbeb', borderColor: '#fde68a', color: '#78350f' },
          standardInfo: { backgroundColor: '#eff6ff', borderColor: '#bfdbfe', color: '#1e3a8a' },
        },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: '#e4e4e7', opacity: 1 } } },
      MuiSkeleton: { styleOverrides: { root: { borderRadius: 10, backgroundColor: '#f4f4f5' } } },
      MuiPaginationItem: {
        styleOverrides: {
          root: { borderRadius: 9999, fontFamily: QUICKSAND, fontWeight: 500, '&.Mui-selected': { backgroundColor: primaryColor, color: primaryContrast, '&:hover': { backgroundColor: primaryPalette[600].hex } } },
        },
      },
      MuiTooltip: {
        styleOverrides: { tooltip: { backgroundColor: '#18181b', color: '#fff', fontFamily: QUICKSAND, fontSize: '0.76rem', borderRadius: 8, padding: '6px 10px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' } },
      },
    },
  });
};

export const defaultMuiTheme = createMuiBrandTheme({});
